export const getCanvasObjectBounds = (obj) => {
  if (!obj) return null;

  obj.setCoords?.();

  if (typeof obj.getBoundingRect === "function") {
    return obj.getBoundingRect(true, true);
  }

  return {
    left: obj.left || 0,
    top: obj.top || 0,
    width: typeof obj.getScaledWidth === "function" ? obj.getScaledWidth() : obj.width || 0,
    height: typeof obj.getScaledHeight === "function" ? obj.getScaledHeight() : obj.height || 0,
  };
};

export const getDesignAreaObject = (canvas) =>
  canvas?.getObjects?.().find((object) => object.designArea === true);

export const repositionDesignAreaObjects = (canvas, previousArea, nextArea) => {
  if (!previousArea?.width || !previousArea?.height || !nextArea?.width || !nextArea?.height) return;
  if (['left', 'top', 'width', 'height'].every((key) => previousArea[key] === nextArea[key])) return;
  canvas.getObjects().forEach((object) => {
    if (!object.designElement) return;
    // Read placement against the old bounds before applying the new bounds.
    syncObjectDesignAreaMetrics(object, previousArea);
    restoreObjectDesignAreaMetrics(object, nextArea);
    object.setCoords();
  });
  canvas.requestRenderAll();
};

export const syncObjectDesignAreaMetrics = (obj, designArea) => {
  if (!obj || !designArea?.width || !designArea?.height) return;

  // Resize restores Fabric's origin and unrotated dimensions, not its bounding box.
  obj.set({
    leftPercent: ((obj.left - designArea.left) / designArea.width) * 100,
    topPercent: ((obj.top - designArea.top) / designArea.height) * 100,
    widthPercent: (obj.getScaledWidth() / designArea.width) * 100,
    heightPercent: (obj.getScaledHeight() / designArea.height) * 100,
    lockScalingFlip: true,
  });
};

export const restoreObjectDesignAreaMetrics = (obj, designArea) => {
  if (!obj || !designArea?.width || !designArea?.height) return;

  const { leftPercent, topPercent, widthPercent, heightPercent } = obj;
  if (![leftPercent, topPercent, widthPercent, heightPercent].every(Number.isFinite)) return;

  const width = obj.getScaledWidth();
  const height = obj.getScaledHeight();
  if (!width || !height) return;

  const scale = Math.min(
    (designArea.width * widthPercent) / (100 * width),
    (designArea.height * heightPercent) / (100 * height)
  );
  obj.set({
    left: designArea.left + (designArea.width * leftPercent) / 100,
    top: designArea.top + (designArea.height * topPercent) / 100,
    scaleX: obj.scaleX * scale,
    scaleY: obj.scaleY * scale,
  });
  constrainObjectToDesignAreaBounds(obj, designArea, null);
  syncObjectDesignAreaMetrics(obj, designArea);
};

export const constrainObjectToDesignAreaBounds = (
  obj,
  designArea,
  canvas = obj?.canvas,
  preferredCenter = null
) => {
  if (!obj || !designArea) return;

  const designBounds = getCanvasObjectBounds(designArea);
  if (!designBounds?.width || !designBounds?.height) return;

  if (preferredCenter) {
    obj.setPositionByOrigin(preferredCenter, "center", "center");
  }

  obj.setCoords?.();
  let objectBounds = getCanvasObjectBounds(obj);
  if (!objectBounds?.width || !objectBounds?.height) return;

  const scaleFactor = Math.min(
    designBounds.width / objectBounds.width,
    designBounds.height / objectBounds.height,
    1
  );

  if (scaleFactor < 1) {
    obj.set({
      scaleX: (obj.scaleX || 1) * scaleFactor,
      scaleY: (obj.scaleY || 1) * scaleFactor,
    });

    if (preferredCenter) {
      obj.setPositionByOrigin(preferredCenter, "center", "center");
    }

    obj.setCoords?.();
    objectBounds = getCanvasObjectBounds(obj);
  }

  const designRight = designBounds.left + designBounds.width;
  const designBottom = designBounds.top + designBounds.height;
  const objectRight = objectBounds.left + objectBounds.width;
  const objectBottom = objectBounds.top + objectBounds.height;
  let deltaX = 0;
  let deltaY = 0;

  if (objectBounds.left < designBounds.left) {
    deltaX = designBounds.left - objectBounds.left;
  } else if (objectRight > designRight) {
    deltaX = designRight - objectRight;
  }

  if (objectBounds.top < designBounds.top) {
    deltaY = designBounds.top - objectBounds.top;
  } else if (objectBottom > designBottom) {
    deltaY = designBottom - objectBottom;
  }

  if (deltaX || deltaY) {
    obj.set({
      left: (obj.left || 0) + deltaX,
      top: (obj.top || 0) + deltaY,
    });
  }

  obj.setCoords?.();

  objectBounds = getCanvasObjectBounds(obj);
  if (!objectBounds?.width || !objectBounds?.height) return;

  const finalScaleFactor = Math.min(
    designBounds.width / objectBounds.width,
    designBounds.height / objectBounds.height,
    1
  );

  if (finalScaleFactor < 1) {
    obj.set({
      scaleX: (obj.scaleX || 1) * finalScaleFactor,
      scaleY: (obj.scaleY || 1) * finalScaleFactor,
    });
    obj.setCoords?.();
    objectBounds = getCanvasObjectBounds(obj);
  }

  const finalObjectRight = objectBounds.left + objectBounds.width;
  const finalObjectBottom = objectBounds.top + objectBounds.height;
  let finalDeltaX = 0;
  let finalDeltaY = 0;

  if (objectBounds.left < designBounds.left) {
    finalDeltaX = designBounds.left - objectBounds.left;
  } else if (finalObjectRight > designRight) {
    finalDeltaX = designRight - finalObjectRight;
  }

  if (objectBounds.top < designBounds.top) {
    finalDeltaY = designBounds.top - objectBounds.top;
  } else if (finalObjectBottom > designBottom) {
    finalDeltaY = designBottom - finalObjectBottom;
  }

  if (finalDeltaX || finalDeltaY) {
    obj.set({
      left: (obj.left || 0) + finalDeltaX,
      top: (obj.top || 0) + finalDeltaY,
    });
    obj.setCoords?.();
  }

  canvas?.renderAll?.();
};
